/**
 * One option choice: read-only by default, flips to an editable row (label, admin-only
 * prompt, reference image) on ✎; 🗑 archives it. Every change persists through
 * `adminUpdateOptionChoice` and reports the server result up via `onChanged` (archived
 * results carry `archived: true` so the parent can drop them).
 *
 * P2 (round-trip audit): the "required" badge + checkbox were REMOVED. `required` round-
 * tripped to the DB and out again through `/api/options/resolved`, but no surface has ever
 * enforced it — GeneratePanel, TemplateEditor and the Wizard all ignore the flag, and the
 * server never rejects a job for a missing "required" choice. A checkbox that promises
 * validation nobody performs is worse than no checkbox (same call as the inert batch
 * background picker we deleted). The column and its API stay; only the false promise goes.
 */
import { useState } from "react";
import { useServices } from "../app/context.js";
import type { OptionChoice } from "@genz/platform";
import { ChoiceReferenceImage } from "./ChoiceReferenceImage.js";
import { RowAction } from "../ui/RowAction.js";

export function ChoiceRow({
  groupId,
  choice,
  onChanged,
  onError,
}: {
  groupId: string;
  choice: OptionChoice;
  onChanged: (updated: OptionChoice) => void;
  onError: (msg: string) => void;
}) {
  const { client } = useServices();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(choice.label);
  const [harness, setHarness] = useState(choice.harness_prompt ?? "");
  const [busy, setBusy] = useState(false);
  const tid = `option-choice-${choice.id}`;

  const fail = (e: unknown) => onError(e instanceof Error ? e.message : String(e));

  async function save() {
    setBusy(true);
    try {
      const updated = await client.adminUpdateOptionChoice(choice.id, {
        label: label.trim() || choice.value,
        harness_prompt: harness.trim() ? harness.trim() : null,
      });
      onChanged(updated);
      setEditing(false);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      const updated = await client.adminUpdateOptionChoice(choice.id, { archived: true });
      onChanged(updated);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function setReference(ref: string | null) {
    try {
      const updated = await client.adminUpdateOptionChoice(choice.id, { reference_ref: ref });
      onChanged(updated);
    } catch (e) {
      fail(e);
    }
  }

  if (!editing) {
    return (
      <div className="options-choice" data-testid={tid}>
        <strong>{choice.label}</strong>
        <span className="faint">({choice.value})</span>
        {choice.harness_prompt && (
          <span className="faint options-choice__prompt" data-testid={`${tid}-harness`} title={choice.harness_prompt}>
            <span aria-hidden="true">·</span> {choice.harness_prompt}
          </span>
        )}
        <ChoiceReferenceImage
          value={choice.reference_ref}
          testid={`${tid}-ref`}
          groupId={groupId}
          choiceValue={choice.value}
          onChange={(ref) => void setReference(ref)}
        />
        {/* WS-Z9: these two were ALREADY icon-only — and had no accessible name at all, on
            either of them, because nothing named the shape. `RowAction` is that shape: the
            same icons, plus the `aria-label` and the tooltip they were missing. */}
        <RowAction label="Edit" icon="editor" tone="quiet" data-testid={`${tid}-edit`}
          onClick={() => setEditing(true)} />
        <RowAction label="Delete" icon="trash" tone="danger" data-testid={`${tid}-delete`}
          disabled={busy} onClick={() => void remove()} />
      </div>
    );
  }

  return (
    <div className="options-choice options-choice--edit" data-testid={tid}>
      <label className="field"><span>Label</span>
        <input className="input" type="text" value={label} data-testid={`${tid}-edit-label`}
          onChange={(e) => setLabel(e.target.value)} /></label>
      <label className="field options-choice__harness"><span>Prompt (admin-only)</span>
        <textarea className="input" rows={2} value={harness} data-testid={`${tid}-edit-harness`}
          onChange={(e) => setHarness(e.target.value)} /></label>
      <ChoiceReferenceImage
        value={choice.reference_ref}
        testid={`${tid}-ref`}
        groupId={groupId}
        choiceValue={choice.value}
        onChange={(ref) => void setReference(ref)}
      />
      {/* WS-Z5 rule 7: a ROW action inside an expanded group, not the panel's primary. */}
      <button type="button" className="btn btn--sm" disabled={busy}
        data-testid={`${tid}-edit-save`} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>
      <button type="button" className="btn btn--sm btn--ghost" data-testid={`${tid}-edit-cancel`}
        onClick={() => {
          setEditing(false);
          setLabel(choice.label);
          setHarness(choice.harness_prompt ?? "");
        }}>Cancel</button>
    </div>
  );
}
